/**
 * ExcelWorkflowMenu — reusable Template / Import / Export dropdown for the
 * Vendor Master + PO Master rich Excel flows.
 *
 * `endpoints` = { template, importUrl, exportUrl, label }.
 * Import runs a preview (`commit=false`) first — the user sees errors and
 * confirms before we write with `commit=true`.
 */
import { useRef, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { FileSpreadsheet, Download, Upload, X, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';

export default function ExcelWorkflowMenu({ endpoints, onImported, testIdPrefix = 'excel' }) {
  const fileRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);

  const download = async (url, name) => {
    try {
      const res = await api.get(url, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl; a.download = name; a.click();
      URL.revokeObjectURL(blobUrl);
      toast.success(`${name} downloaded`);
    } catch { toast.error('Download failed'); }
  };

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setOpen(false);
    runPreview(f);
  };

  const runPreview = async (f) => {
    setBusy(true); setPreview(null);
    try {
      const fd = new FormData(); fd.append('file', f);
      const { data } = await api.post(`${endpoints.importUrl}?commit=false`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(data);
      setPreviewOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    }
    setBusy(false);
  };

  const commit = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await api.post(`${endpoints.importUrl}?commit=true`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Imported ${data.okRows} row(s). ${data.errorRows} row(s) skipped.`);
      setPreviewOpen(false); setFile(null);
      onImported?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed');
    }
    setBusy(false);
  };

  return (
    <>
      <div className="relative inline-block" data-testid={`${testIdPrefix}-menu`}>
        <Button size="sm" variant="outline" onClick={() => setOpen(o => !o)} disabled={busy}
          data-testid={`${testIdPrefix}-btn`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5 mr-1" />}
          Excel
        </Button>
        {open && (
          <div className="absolute right-0 top-full mt-1 z-30 min-w-[220px] rounded shadow-lg border"
            style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
            <button onClick={() => { download(endpoints.template, endpoints.templateName || 'template.xlsx'); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-black/5"
              data-testid={`${testIdPrefix}-download-template`}>
              <Download className="w-3.5 h-3.5" style={{ color: 'var(--tone-navy-text)' }} /> Download Template
            </button>
            <button onClick={() => { fileRef.current?.click(); }}
              className="w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-black/5"
              data-testid={`${testIdPrefix}-import`}>
              <Upload className="w-3.5 h-3.5" style={{ color: '#F26B1F' }} /> Import xlsx…
            </button>
            <button onClick={() => { download(endpoints.exportUrl, endpoints.exportName || 'export.xlsx'); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-black/5"
              data-testid={`${testIdPrefix}-export`}>
              <Download className="w-3.5 h-3.5" style={{ color: '#10B981' }} /> Export Current Data
            </button>
          </div>
        )}
        <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={pickFile}
          data-testid={`${testIdPrefix}-file-input`} />
      </div>

      {previewOpen && preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.55)' }}
          onClick={() => setPreviewOpen(false)} data-testid={`${testIdPrefix}-preview-dialog`}>
          <div className="w-full max-w-2xl max-h-[85vh] rounded-xl shadow-2xl flex flex-col"
            style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }} onClick={e => e.stopPropagation()}>
            <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: 'var(--border-color)' }}>
              <div>
                <div className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>
                  Import Preview — {endpoints.label || 'records'}
                </div>
                <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                  <b style={{ color: '#10B981' }}>{preview.okRows}</b> valid ·
                  <b style={{ color: '#EF4444' }}> {preview.errorRows}</b> with errors ·
                  {preview.total} total
                </div>
              </div>
              <button onClick={() => setPreviewOpen(false)}><X className="w-5 h-5" style={{ color: 'var(--text-muted)' }} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <table className="w-full text-[11px]">
                <thead style={{ background: 'var(--card-bg-alt)' }}>
                  <tr>
                    <th className="p-1.5 text-left">Row</th>
                    <th className="p-1.5 text-left">Key</th>
                    <th className="p-1.5 text-left">Status</th>
                    <th className="p-1.5 text-left">Issues</th>
                  </tr>
                </thead>
                <tbody>
                  {(preview.rows || []).map((r, i) => (
                    <tr key={i} style={{ borderTop: '1px solid var(--border-color)' }}
                      data-testid={`${testIdPrefix}-preview-row-${i}`}>
                      <td className="p-1.5 font-mono">{r.row}</td>
                      <td className="p-1.5">{r.legalName || r.poNumber || r.mciLineNumber || '—'}</td>
                      <td className="p-1.5">
                        {r.ok ? <span className="inline-flex items-center gap-1 text-[10px] px-1.5 rounded font-bold" style={{ background: 'var(--tone-green-bg)', color: 'var(--tone-green-text)' }}><CheckCircle2 className="w-3 h-3" /> OK</span>
                              : <span className="inline-flex items-center gap-1 text-[10px] px-1.5 rounded font-bold" style={{ background: 'var(--tone-red-bg)', color: 'var(--tone-red-text)' }}><AlertTriangle className="w-3 h-3" /> Error</span>}
                      </td>
                      <td className="p-1.5" style={{ color: '#EF4444' }}>{(r.errors || []).join('; ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 border-t flex justify-end gap-2" style={{ borderColor: 'var(--border-color)' }}>
              <Button variant="ghost" onClick={() => setPreviewOpen(false)}>Cancel</Button>
              <Button onClick={commit} disabled={busy || preview.okRows === 0}
                data-testid={`${testIdPrefix}-preview-commit`}
                className="text-white font-semibold" style={{ background: '#F26B1F' }}>
                {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                Commit {preview.okRows} valid row(s)
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
